/**
 * Policies Page
 *
 * Main landing page for policy management. Displays policies in tabs:
 * - Active: Currently enforced policies
 * - Drafts: Policies being configured
 * - Archived: Disabled/archived policies
 * - Activity: Audit log of policy triggers and actions
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSessionJwt } from '../hooks/useSessionJwt';
import { Plus, Shield, Activity } from 'lucide-react';
import { useEnvironment } from '../contexts/EnvironmentContext';
import ViewTabs from '../components/ViewTabs';
import { PolicyCard, PolicyWizard, PolicyDetailDrawer, PolicyActivationModal } from '../components';
import { spacing, components } from '../utils/designTokens';

// Tab configuration
const TABS = [
  { value: 'active', label: 'Active' },
  { value: 'drafts', label: 'Drafts' },
  { value: 'archived', label: 'Archived' },
  { value: 'activity', label: 'Activity' },
];

const Policies = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [policies, setPolicies] = useState([]);
  const [formOptions, setFormOptions] = useState({ metrics: [], actions: [], apps: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activatingPolicy, setActivatingPolicy] = useState(null);

  const { selectedEnvironment } = useEnvironment();
  const { sessionId, getJwt } = useSessionJwt();

  // Fetch form options (metrics, actions, apps) for the PolicyWizard
  const fetchFormOptions = useCallback(async () => {
    const jwt = getJwt();
    if (!selectedEnvironment || !jwt) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(
        `${apiUrl}/data/policies/form-options?environment=${encodeURIComponent(selectedEnvironment)}`,
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch form options: ${response.status}`);
      }

      const data = await response.json();
      setFormOptions({
        metrics: data.metrics || [],
        actions: data.actions || [],
        apps: data.apps || []
      });
    } catch (err) {
      console.error('Error fetching policy form options:', err);
    }
  }, [selectedEnvironment, sessionId, getJwt]);

  // Fetch form options when modal opens or environment changes
  useEffect(() => {
    if (showCreateModal || showActivationModal) {
      fetchFormOptions();
    }
  }, [showCreateModal, showActivationModal, fetchFormOptions]);

  // Fetch policies from API
  const fetchPolicies = useCallback(async () => {
    const jwt = getJwt();
    if (!jwt) return;

    setLoading(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/data/policies`, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch policies: ${response.status}`);
      }

      const data = await response.json();
      setPolicies(data.policies || []);
    } catch (err) {
      console.error('Error fetching policies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId, getJwt]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  // Filter policies based on active tab
  const filteredPolicies = useMemo(() => {
    if (activeTab === 'activity') return []; // Activity tab shows different content

    if (activeTab === 'archived') {
      // Archived: policies that are deleted or have archived status
      return policies.filter(p => p.is_deleted || p.status === 'archived');
    }

    if (activeTab === 'active') {
      // Active: policies that are active in at least one environment
      return policies.filter(p => !p.is_deleted && (p.active_environments?.length > 0 || p.status === 'active'));
    }

    if (activeTab === 'drafts') {
      // Drafts: policies not active in any environment and not archived/deleted
      return policies.filter(p => !p.is_deleted && p.status !== 'archived' && (!p.active_environments || p.active_environments.length === 0) && p.status !== 'active');
    }

    return policies.filter(p => !p.is_deleted);
  }, [policies, activeTab]);

  const handleCreatePolicy = () => {
    setEditingPolicy(null);
    setShowCreateModal(true);
  };

  const handleSelectPolicy = (policy) => {
    setSelectedPolicy(policy);
  };

  const handleCloseDrawer = () => {
    setSelectedPolicy(null);
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setSelectedPolicy(null);
    setShowCreateModal(true);
  };

  const handleArchivePolicy = async (policy) => {
    const jwt = getJwt();
    if (!jwt) {
      console.error('No session token available');
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/data/policies/${policy.policy_id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to archive policy: ${response.status}`);
      }

      // Refresh policies list
      await fetchPolicies();
      setSelectedPolicy(null);
    } catch (err) {
      console.error('Error archiving policy:', err);
    }
  };

  // Show activation modal when user wants to activate a policy
  const handleShowActivationModal = (policy) => {
    setActivatingPolicy(policy);
    setShowActivationModal(true);
    setSelectedPolicy(null); // Close the detail drawer
  };

  // Handle actual activation API call (called from modal)
  const handleActivatePolicy = async ({ policy, environment, version, app_id }) => {
    const jwt = getJwt();
    if (!jwt) {
      throw new Error('No session token available');
    }

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    const response = await fetch(`${apiUrl}/data/policies/${policy.policy_id}/activate`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        environment,
        version,
        ...(app_id && { app_id }),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to activate policy: ${response.status}`);
    }

    console.log(`Policy ${policy.policy_id} activated in ${environment} (version ${version})`);

    // Refresh policies list
    await fetchPolicies();
    setShowActivationModal(false);
    setActivatingPolicy(null);
  };

  // Handle deactivation (called from drawer)
  const handleDeactivatePolicy = async (policy) => {
    const jwt = getJwt();
    if (!jwt) {
      console.error('No session token available');
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const activeEnvs = policy.active_environments || [];

    try {
      for (const env of activeEnvs) {
        const response = await fetch(`${apiUrl}/data/policies/${policy.policy_id}/deactivate`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ environment: env }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to deactivate policy: ${response.status}`);
        }
      }
      console.log(`Policy ${policy.policy_id} deactivated in environments:`, activeEnvs);

      // Refresh policies list
      await fetchPolicies();
      setSelectedPolicy(null);
    } catch (err) {
      console.error('Error deactivating policy:', err);
    }
  };

  // Toggle status: show modal for activation, deactivate directly
  const handleTogglePolicyStatus = (policy) => {
    const isActive = policy.status === 'active';
    if (isActive) {
      handleDeactivatePolicy(policy);
    } else {
      handleShowActivationModal(policy);
    }
  };

  const handleSavePolicy = async (formData) => {
    const jwt = getJwt();
    if (!jwt) {
      console.error('No session token available');
      return;
    }

    // Transform wizard formData to API payload
    // Note: Scope (environment + app) is applied at activation time, not here
    const payload = {
      name: formData.name,
      description: formData.description || '',
      metric_type: formData.metric.type,
      metric_comparator: formData.metric.comparator,
      metric_threshold: formData.metric.threshold,
      metric_window: formData.metric.window,
      actions: formData.actions,
    };

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const isEditing = !!editingPolicy;
      const url = isEditing
        ? `${apiUrl}/data/policies/${editingPolicy.policy_id}`
        : `${apiUrl}/data/policies`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to save policy: ${response.status}`);
      }

      // Refresh policies list
      await fetchPolicies();

      setShowCreateModal(false);
      setEditingPolicy(null);
    } catch (err) {
      console.error('Error saving policy:', err);
      // Could add user-facing error handling here
      throw err; // Re-throw so PolicyWizard can handle it
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={components.table.empty}>
          Loading policies...
        </div>
      );
    }

    if (error) {
      return (
        <div className={components.table.empty}>
          <div className="text-status-danger">{error}</div>
        </div>
      );
    }

    if (activeTab === 'activity') {
      // Activity tab - will show PolicyActivityTable
      return (
        <div className="flex flex-col items-center justify-center h-full px-4 py-8 text-center">
          <Activity size={48} className="text-text-muted mb-3" />
          <div className="text-sm text-text-primary font-medium mb-1">
            No policy activity
          </div>
          <div className="text-xs text-text-muted mb-4">
            Policy triggers and actions will appear here once policies are configured and activated.
          </div>
        </div>
      );
    }

    if (filteredPolicies.length === 0) {
      const emptyMessages = {
        active: {
          title: 'No active policies',
          description: 'Activate a draft policy to start monitoring your AI agents.',
          ctaLabel: 'Go to Drafts',
          ctaAction: () => setActiveTab('drafts'),
        },
        drafts: {
          title: 'No draft policies',
          description: 'Create a policy to start monitoring your AI agents for quality, security, and performance issues.',
          ctaLabel: 'Create Policy',
          ctaAction: handleCreatePolicy,
        },
        archived: {
          title: 'No archived policies',
          description: 'Archived policies will appear here when you disable them.',
          ctaLabel: null,
          ctaAction: null,
        },
      };

      const { title, description, ctaLabel, ctaAction } = emptyMessages[activeTab] || emptyMessages.active;

      return (
        <div className="flex flex-col items-center justify-center h-full px-4 py-8 text-center">
          <Shield size={48} className="text-text-muted mb-3" />
          <div className="text-sm text-text-primary font-medium mb-1">
            {title}
          </div>
          <div className="text-xs text-text-muted mb-4">
            {description}
          </div>
          {ctaLabel && ctaAction && (
            <button
              onClick={ctaAction}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent-primary-base text-background-primary rounded hover:opacity-90 transition-opacity"
            >
              <Plus size={14} />
              {ctaLabel}
            </button>
          )}
        </div>
      );
    }

    // Render policy cards
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPolicies.map((policy) => (
          <PolicyCard
            key={policy.policy_id}
            policy={policy}
            onSelect={handleSelectPolicy}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={`flex flex-col h-full ${spacing.gap.md}`}>
      {/* Header section */}
      <div className="flex-shrink-0 flex items-center justify-between gap-4 mb-12">
        {/* Left side: Title + Tabs */}
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-bold text-text-primary">
            Policies
          </h1>
          <ViewTabs
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>

        {/* Right side: Create button (drafts only) */}
        {activeTab === 'drafts' && (
          <button
            onClick={handleCreatePolicy}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-accent-primary-base text-background-primary hover:opacity-90 transition-opacity"
          >
            <Plus size={14} />
            Create Policy
          </button>
        )}
      </div>

      {/* Main content area */}
      <div className="flex-1 min-h-0 overflow-auto">
        {renderContent()}
      </div>

      {/* Policy Wizard Modal */}
      <PolicyWizard
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingPolicy(null);
        }}
        onSave={handleSavePolicy}
        existingPolicy={editingPolicy}
        metrics={formOptions.metrics}
        actions={formOptions.actions}
      />

      {/* Policy Detail Drawer */}
      <PolicyDetailDrawer
        policy={selectedPolicy}
        isOpen={!!selectedPolicy}
        onClose={handleCloseDrawer}
        onEdit={handleEditPolicy}
        onArchive={handleArchivePolicy}
        onToggleStatus={handleTogglePolicyStatus}
      />

      {/* Policy Activation Modal */}
      <PolicyActivationModal
        policy={activatingPolicy}
        isOpen={showActivationModal}
        onClose={() => {
          setShowActivationModal(false);
          setActivatingPolicy(null);
        }}
        onActivate={handleActivatePolicy}
        apps={formOptions.apps}
      />
    </div>
  );
};

export default Policies;
